import React, { useState, useEffect, useRef } from 'react';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import {
  Mic,
  MessageCircle,
  History,
  FileText,
  Lightbulb,
  Home,
  BarChart3,
  LayoutDashboard,
  NotebookPen,
  BookOpen,
  Zap,
} from 'lucide-react';

const MobileNavigation = ({ 
  tabs = [], 
  activeTab, 
  onTabChange, 
  className = "",
  showLabels = true,
  compact = false,
  /** Optional: provide the element that actually scrolls (e.g. messages container) */
  getScrollElement,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navRef = useRef(null);

  // Auto-hide navigation on scroll (mobile only)
  // IMPORTANT: In our widget, the messages pane scrolls (not window), so we support a custom scroll element.
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      setIsVisible(true);
      return;
    }

    const el = typeof getScrollElement === 'function'
      ? getScrollElement()
      : null;

    const target = el || window;
    let ticking = false;

    const readScrollTop = () => {
      if (el) return el.scrollTop || 0;
      return window.scrollY || 0;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const current = readScrollTop();
        const delta = current - lastScrollY;

        // Hide only when scrolling down with intent, show when scrolling up
        if (current > 80 && delta > 8) setIsVisible(false);
        if (delta < -6) setIsVisible(true);

        setLastScrollY(current);
        ticking = false;
      });
    };

    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [getScrollElement, lastScrollY]);

  // Keyboard navigation
  const { containerRef } = useKeyboardNavigation({
    enableArrowKeys: true,
    enableTabNavigation: true,
    onEnter: (element) => {
      const tabIndex = element.getAttribute('data-tab-index');
      if (tabIndex !== null) {
        onTabChange(tabs[parseInt(tabIndex)]?.id);
      }
    }
  });

  const handleTabClick = (tabId, index) => {
    onTabChange(tabId);
    
    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  // Swipe gesture handling
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
      let newIndex;
      
      if (isLeftSwipe && currentIndex < tabs.length - 1) {
        newIndex = currentIndex + 1;
      } else if (isRightSwipe && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }
      
      if (newIndex !== undefined) {
        onTabChange(tabs[newIndex].id);
        
        // Haptic feedback for swipe
        if ('vibrate' in navigator) {
          navigator.vibrate(30);
        }
      }
    }
  };

  const getTabIcon = (tab) => {
    // If tab.icon is explicitly provided as a string, preserve it (backward compatible).
    if (typeof tab.icon === 'string') return tab.icon;

    const icons = {
      voice: Mic,
      chat: MessageCircle,
      history: History,
      coach: NotebookPen,
      docs: FileText,
      documents: BookOpen,
      insights: Zap,
      home: Home,
      sessions: LayoutDashboard,
      progress: BarChart3,
    };

    return icons[tab.id] || FileText;
  };

  const getTabLabel = (tab) => {
    if (compact) return '';
    return showLabels ? tab.label || tab.id : '';
  };

  return (
    <nav 
      ref={containerRef}
      className={`ov-mobile-nav ${className} ${isVisible ? 'ov-nav-visible' : 'ov-nav-hidden'} ${compact ? 'ov-nav-compact' : ''}`}
      role="tablist"
      aria-label="Main navigation"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="ov-mobile-nav-container">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={index === 0 ? navRef : null}
            className={`ov-mobile-nav-tab ${activeTab === tab.id ? 'ov-nav-tab-active' : ''}`}
            onClick={() => handleTabClick(tab.id, index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTabClick(tab.id, index);
              }
            }}
            data-tab-index={index}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            aria-label={tab.label || tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            <span className="ov-nav-icon" aria-hidden="true">
              {(() => {
                const Icon = getTabIcon(tab);
                return typeof Icon === 'string' ? Icon : <Icon size={18} strokeWidth={2.5} stroke="currentColor" fill="none" />;
              })()}
            </span>
            {getTabLabel(tab) && (
              <span className="ov-nav-label">
                {getTabLabel(tab)}
              </span>
            )}
            {tab.badge && (
              <span className="ov-nav-badge" aria-label={`${tab.badge} notifications`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Powered by OpsIQ branding */}
      <div className="ov-mobile-nav-branding">
        Powered by <span className="ov-nav-brand-name">OpsIQ</span>
      </div>
    </nav>
  );
};

export default MobileNavigation;
